import { useQueryClient } from '@tanstack/react-query'

import { useIndividualsServiceDeleteV1IndividualsByIndividualIdDocumentsByDocumentId } from '../client/queries'
import {
  UseTimelineServiceGetV1IndividualsByIndividualIdTimelineKeyFn,
  useErrandsServiceGetV1IndividualsByIndividualIdErrandsKey,
  useIndividualsServiceGetV1IndividualsByIndividualIdKey,
  type IndividualsServiceDeleteV1IndividualsByIndividualIdDocumentsByDocumentIdMutationResult,
} from '../client/queries/common'
import {
  optimisticallyEditDocuments,
  rollbackDocuments,
  type IndividualSnapshots,
} from './optimistic-documents'

export const useDeleteDocument = () => {
  const queryClient = useQueryClient()

  return useIndividualsServiceDeleteV1IndividualsByIndividualIdDocumentsByDocumentId<
    IndividualsServiceDeleteV1IndividualsByIndividualIdDocumentsByDocumentIdMutationResult,
    unknown,
    IndividualSnapshots
  >({
    onError: (_error, _variables, context) => rollbackDocuments(queryClient, context),
    // Drop the document from the cache on click. The endpoint is idempotent, so
    // a repeat delete of a row the user out-clicked still succeeds and there is
    // nothing to roll back.
    onMutate: ({ documentId }) =>
      optimisticallyEditDocuments(queryClient, (documents) =>
        documents.filter((d) => d.id !== documentId),
      ),
    onSettled: (_data, _error, { individualId }) => {
      queryClient.invalidateQueries({
        queryKey: [useIndividualsServiceGetV1IndividualsByIndividualIdKey],
      })
      queryClient.invalidateQueries({
        queryKey: [useErrandsServiceGetV1IndividualsByIndividualIdErrandsKey],
      })
      queryClient.invalidateQueries({
        queryKey: UseTimelineServiceGetV1IndividualsByIndividualIdTimelineKeyFn({ individualId }),
      })
    },
  })
}
